import * as React from 'react';
import {Mutation} from 'react-apollo';

import type {EmailAutomationField} from 'nutshell-graphql-types';
import type {
    EmailAutomationStatus as EmailAutomationStatusFragment,
    GetEmailAutomationsReadyToSend_emailAutomationStatusesReadyToSend_edges as EmailAutomationStatusEdge,
    EmailAutomationStatusUpdateField as EmailAutomationStatusUpdateFieldMutation,
    EmailAutomationStatusUpdateFieldVariables as EmailAutomationStatusUpdateFieldMutationVariables,
} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';

import EMAIL_AUTOMATION_STATUS_UPDATE_FIELD from './graphql/mutations/email-automation-status-update-field.graphql';

class UpdateStatusFieldMutation extends Mutation<
    EmailAutomationStatusUpdateFieldMutation,
    EmailAutomationStatusUpdateFieldMutationVariables
> {}

type Props = {
    children: (arg1: {
        onUpdateStatusField: (
            emailAutomationStatus: any,
            field: EmailAutomationField,
            newValue?: string | null | undefined
        ) => Promise<any>;
    }) => React.ReactElement;
};

export function UpdateEmailAutomationStatusField(props: Props) {
    return (
        <UpdateStatusFieldMutation mutation={EMAIL_AUTOMATION_STATUS_UPDATE_FIELD}>
            {(emailAutomationStatusUpdateField) =>
                props.children({
                    onUpdateStatusField: (
                        emailAutomationStatus: EmailAutomationStatusEdge,
                        field,
                        newValue
                    ) => {
                        const optimisticResponse = emailAutomationStatus
                            ? {
                                  __typename: 'Mutation',
                                  emailAutomationStatusUpdateField: {
                                      __typename: 'EmailAutomationStatusUpdateFieldPayload',
                                      emailAutomationStatus: updateEmailAutomationStatusWithNewData(
                                          emailAutomationStatus,
                                          field,
                                          newValue
                                      ),
                                  },
                              }
                            : undefined;

                        return emailAutomationStatusUpdateField({
                            variables: {
                                input: {
                                    id: emailAutomationStatus.node.id,
                                    field: field,
                                    value: newValue,
                                },
                            },
                            // @ts-expect-error
                            optimisticResponse,
                        }).then(() => {
                            analyticsClient.track(
                                analyticsClient.EVENTS
                                    .TRACK_AUTOMATION_STATUS_FIELD_UPDATED_FROM_LIST
                            );
                        });
                    },
                })
            }
        </UpdateStatusFieldMutation>
    );
}

// Optimistic response helper - add test once we get email address in here
function updateEmailAutomationStatusWithNewData(
    emailAutomationStatus: EmailAutomationStatusEdge,
    field: EmailAutomationField,
    newValue?: string | null
): EmailAutomationStatusFragment {
    if (
        field === 'FIRST_NAME' ||
        field === 'LAST_NAME' ||
        field === 'EMAIL_ADDRESS' ||
        field === 'COMPANY_NAME'
    ) {
        const fieldNameMap = {
            FIRST_NAME: 'firstName',
            LAST_NAME: 'lastName',
            EMAIL_ADDRESS: 'emailAddress',
            COMPANY_NAME: 'companyName',
        }[field];

        return {
            ...emailAutomationStatus.node,
            [fieldNameMap]: {
                // @ts-expect-error
                ...emailAutomationStatus.node[fieldNameMap],
                value: newValue,
            },
        };
    }

    return {
        ...emailAutomationStatus.node,
    };
}
